import type { Scale } from '@tunasong/schemas'
import type { Chord } from '../chord/index.js'
import { getDiatonicFunction } from './diatonic.js'
import { PROGRESSION_LIBRARY } from './progression-library.js'
import type { ChordFunction } from './types.js'

/**
 * Named-progression match over the last 4–8 chords. Wired in Milestone 5.
 */
export interface ProgressionTag {
  id: string
  span: [number, number]
  lexiconId: string
}

/**
 * Minimum window length for a progression match. Two-chord fragments are
 * intentionally rejected (per Bowie's Notion task: "Don't match on 2-chord
 * fragments — too ambiguous"). The shortest pattern in the Phase-1 library is
 * ii-V-I (3 chords).
 */
const MIN_WINDOW = 3

/**
 * Match the trailing chord window against the curated progression library.
 *
 * Phase-1 contract (Les + Bowie):
 *   - Function-blind, transposition-blind — matches happen on Roman-numeral
 *     functions (`getDiatonicFunction` output), not literal chord names.
 *   - Cyclic rotation tolerated — a user playing `V–vi–IV–I` matches the
 *     `I–V–vi–IV` pattern (rotation offset 1). Direct (offset-0) matches are
 *     preferred over rotated matches so distinct library entries (e.g.
 *     `vi–IV–I–V` vs `I–V–vi–IV`) don't collide on the same chord sequence.
 *   - Mode-guarded — each library entry declares `expectedScaleType`; a
 *     pattern named in major-key terms (`ii–V–I`) cannot match a minor or
 *     modal scale. Phase 1 ships major-only, so any minor / modal input
 *     deterministically returns null.
 *   - Trailing-window anchored — the match is anchored at the end of the
 *     chord sequence. This mirrors the cadence detector ("last 2 chords")
 *     and aligns with the line-end chip placement (Joni §2.2).
 *   - Returns null when the scale is undefined, the input is shorter than
 *     `MIN_WINDOW`, any chord in the trailing window is non-diatonic, or no
 *     entry matches.
 *
 * Returns a `ProgressionTag` with the lexicon id and the [start, end]
 * inclusive span into the input `chords` array.
 */
export const matchProgression = (chords: Chord[], scale: Scale | undefined): ProgressionTag | null => {
  if (!scale || chords.length < MIN_WINDOW) {
    return null
  }

  // Phase 1: scale.type may be undefined in older configs; treat that as
  // "no match" — modal/minor guards rely on a concrete type.
  const scaleType = scale.type
  if (!scaleType) {
    return null
  }

  // Two-pass strategy: prefer direct (offset-0) matches over rotated matches
  // so distinct library entries don't collide. Within each pass, library
  // order is the deterministic tie-break.
  const directHit = findMatch(chords, scale, scaleType, /* allowRotation */ false)
  if (directHit) {
    return directHit
  }
  return findMatch(chords, scale, scaleType, /* allowRotation */ true)
}

const findMatch = (
  chords: Chord[],
  scale: Scale,
  scaleType: NonNullable<Scale['type']>,
  allowRotation: boolean
): ProgressionTag | null => {
  for (const entry of PROGRESSION_LIBRARY) {
    if (!entry.expectedScaleType.includes(scaleType)) {
      continue
    }
    const patternLen = entry.pattern.length
    if (chords.length < patternLen) {
      continue
    }
    // Trailing window of length `patternLen`.
    const start = chords.length - patternLen
    const window = chords.slice(start)
    const functions = window.map(c => getDiatonicFunction(c, scale))
    if (functions.some(f => f === null)) {
      continue
    }
    const fnList = functions as ChordFunction[]
    if (matchesPattern(fnList, entry.pattern, allowRotation)) {
      return {
        id: entry.id,
        span: [start, chords.length - 1],
        lexiconId: entry.id,
      }
    }
  }
  return null
}

/**
 * Pattern match. When `allowRotation` is false, only the zero-rotation
 * (literal) match counts. When true, any cyclic rotation of `pattern`
 * matches.
 */
const matchesPattern = (functions: ChordFunction[], pattern: ChordFunction[], allowRotation: boolean): boolean => {
  if (functions.length !== pattern.length) {
    return false
  }
  if (!allowRotation) {
    return pattern.every((p, i) => functions[i] === p)
  }
  const m = pattern.length
  for (let shift = 0; shift < m; shift++) {
    const ok = pattern.every((_, i) => functions[i] === pattern[(i + shift) % m])
    if (ok) {
      return true
    }
  }
  return false
}
